import { Navigate, useLocation } from "react-router-dom";
import { useUser } from "../hooks/useUser";

interface AuthGuardProps {
  children: React.ReactNode;
  noAuth?: boolean;
}

export const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  noAuth = false,
}) => {
  const { user, loading, error } = useUser();
  const location = useLocation();

  console.log("🛡️ AuthGuard: Checking auth state", {
    hasUser: !!user,
    userId: user?.id,
    loading,
    error,
    noAuth,
    currentPath: location.pathname,
    fromPath: location.state?.from?.pathname,
  });

  if (loading) {
    console.log("⏳ AuthGuard: Still loading, showing loading screen");
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    console.error("❌ AuthGuard: Auth error occurred:", error);
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 mb-4">Authentication error occurred</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!user && !noAuth) {
    console.log(
      "🚫 AuthGuard: No user and auth required, redirecting to /auth",
      {
        currentPath: location.pathname,
        noAuth,
      }
    );
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  if (user && noAuth) {
    console.log(
      "✅ AuthGuard: User exists but noAuth=true, redirecting to dashboard",
      {
        userId: user.id,
        currentPath: location.pathname,
      }
    );
    return <Navigate to="/dashboard" replace />;
  }

  console.log("✅ AuthGuard: Auth check passed, rendering children", {
    hasUser: !!user,
    userId: user?.id,
    noAuth,
    currentPath: location.pathname,
  });

  return <>{children}</>;
};
